/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */
/**
 * ABA ANÁLISE — RECRIADA em 19/10 sobre BOT × DIA.
 *
 * > "a questão que eu quero ver é as etapas de conclusão, por dia, e uma análise
 * > dos bots para eu entender os fluxos por dia de cada um, os gaps, os que já
 * > concluíram mais empresas em menos tempo … e ver isso como o fluxo que
 * > organizei" + "com e sem movimento" + "melhores bots por analista que tem
 * > mais volume".
 *
 * A versão anterior agregava etapas só por `modulo_id`, que é POR BOT — "Módulo 2"
 * somava o passo 2 de todos os bots e não significava nada. Esta lê o que cada
 * bot FEZ em cada dia (`/bots-por-dia`, de `bot_unit_result` + execuções) e
 * organiza tudo na ORDEM DO PROCESSOS (`utils/ordemDoFluxo`).
 *
 * Seções, de cima para baixo: destaques · fluxo por dia (quem fechou a última
 * etapa) · mapa bot × dia · ranking · gaps · analistas. Toda regra mora em
 * `utils/analiseBots` (testada); aqui é só desenho.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getBotsPorDia,
  getCobertura,
  getEsteira,
  getMovimento,
  IBotDia,
  IEmpresaResumo,
  IPeriodoParams,
  IResponsaveisPorEmpresa,
  listarDepartamentos,
  STALE_TEMPO_REAL,
} from '../api';
import { C, FONTE_MONO, FONTE_TEXTO, TONES } from '../paleta';
import { useApelidos } from '../useApelidos';
import { nomeEmpresa } from '../utils/derivarProcessos';
import { competenciaDoMovimento } from '../utils/painelDoNo';
import { ordemDoFluxo } from '../utils/ordemDoFluxo';
import { areaCoerente, indexarAreas } from '../utils/derivarCobertura';
import {
  AMOSTRA_MINIMA,
  BlocoFluxo,
  blocosPorFluxo,
  CriterioRanking,
  destaques as calcularDestaques,
  duracaoCurta,
  fluxosPorDia,
  gapsDosBots,
  LinhaBot,
  blocosSemRepeticao,
  linhasPorBot,
  ordenarRanking,
  porAnalista,
  RecorteMovimento,
  rotuloDoDia,
  tomDaCelula,
  TOTAL_DOS_FLUXOS,
  totalDosFluxos,
  type UniversosDosFluxos,
} from '../utils/analiseBots';
import {
  CARTAO,
  estiloPilula,
  EYEBROW,
  Faixa,
  fundoDaAba,
  Icone,
  MATIZ_DA_ABA,
  TituloSecao,
  VIDRO,
} from '../components/Vidro';

interface Props {
  params: IPeriodoParams;
  chavePeriodo: string;
  /** Início do período da tela (YYYY-MM-DD) — define a competência do movimento. */
  periodoDe?: string;
  /** Empresas da página — nomes no detalhe e responsável para os analistas. */
  empresas?: IEmpresaResumo[];
  /** O recorte dos filtros do Processos (regime, empresa, status, os do Resumo). */
  empresasFiltradas?: IEmpresaResumo[];
  filtroAtivo?: boolean;
  /** Os filtros ativos em texto, para a tela dizer qual recorte está valendo. */
  descricaoDosFiltros?: string[];
  responsaveis?: IResponsaveisPorEmpresa;
}

const H = MATIZ_DA_ABA.analise;

const fmt = new Intl.NumberFormat('pt-BR');
const fmt1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

type TomCelula = ReturnType<typeof tomDaCelula>;

/** Cores da célula do mapa — mesma escala de % do painel do Processos. */
const COR_CELULA: Record<TomCelula, { bg: string; fg: string; borda: string }> = {
  vazio: { bg: 'transparent', fg: C.faint, borda: '1px dashed oklch(0.86 0.008 165)' },
  idle: { bg: TONES.idle.bg, fg: TONES.idle.fg, borda: '1px solid transparent' },
  err: { bg: 'oklch(0.9 0.07 28)', fg: C.redFg, borda: '1px solid transparent' },
  warn: { bg: 'oklch(0.92 0.08 75)', fg: C.amberFg, borda: '1px solid transparent' },
  run: { bg: 'oklch(0.9 0.05 255)', fg: C.blueFg, borda: '1px solid transparent' },
  ok: { bg: 'oklch(0.86 0.1 160)', fg: C.greenFg, borda: '1px solid transparent' },
};

const horaLocal = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
};

const PILULA: React.CSSProperties = { padding: '4px 11px' };

export const AnaliseTab: React.FC<Props> = ({
  params,
  chavePeriodo,
  periodoDe,
  empresas = [],
  empresasFiltradas = [],
  filtroAtivo = false,
  descricaoDosFiltros = [],
  responsaveis,
}) => {
  const { apelidoDoBot } = useApelidos();
  const [recorte, setRecorte] = useState<RecorteMovimento>('todos');
  const [fluxosSel, setFluxosSel] = useState<string[]>([]);
  const [criterio, setCriterio] = useState<CriterioRanking>('concluidas');
  const [detalhe, setDetalhe] = useState<{ linha: LinhaBot; dia: string } | null>(null);

  const {
    data: resp,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['orqBotsPorDia', chavePeriodo],
    queryFn: () => getBotsPorDia(params),
    staleTime: STALE_TEMPO_REAL,
  });
  // Mesmas queries (e cache) do Processos: a ordem é a que o dono organizou lá.
  const { data: esteira } = useQuery({
    queryKey: ['orqEsteira', chavePeriodo],
    queryFn: () => getEsteira(params),
    staleTime: STALE_TEMPO_REAL,
  });
  const { data: departamentos } = useQuery({
    queryKey: ['orqDepDepartamentos'],
    queryFn: () => listarDepartamentos(),
  });
  const competenciaMov = useMemo(() => competenciaDoMovimento(periodoDe ?? null), [periodoDe]);
  const { data: movimento } = useQuery({
    queryKey: ['orqMovimento', competenciaMov],
    queryFn: () => getMovimento(competenciaMov as string),
    enabled: Boolean(competenciaMov),
    staleTime: 5 * 60 * 1000,
  });
  const comMovimento = useMemo(
    () => (movimento ? new Set(movimento.com_movimento) : null),
    [movimento],
  );

  // O universo de cada fluxo — a MESMA query (e cache) do Processos. É o total
  // do cartão: sem ele a Análise contaria quem rodou fora do regime da área.
  const { data: cobertura } = useQuery({
    queryKey: ['orqCobertura', chavePeriodo],
    queryFn: () => getCobertura(params),
    staleTime: STALE_TEMPO_REAL,
  });
  // Os FILTROS do Processos (regime, empresa, status e os do Resumo): o mesmo
  // `empresasFiltradas` que recorta o universo do cartão lá (`codisNoRecorte`).
  const noRecorte = useMemo(
    () => (filtroAtivo ? new Set(empresasFiltradas.map(e => e.codi_emp)) : null),
    [filtroAtivo, empresasFiltradas],
  );
  const universos = useMemo(() => {
    const m: UniversosDosFluxos = new Map();
    indexarAreas(cobertura).forEach((area, nome) => {
      if (areaCoerente(area) && area.universo_empresas?.length) {
        m.set(nome, new Set(area.universo_empresas));
      }
    });
    return m;
  }, [cobertura]);

  const ordem = useMemo(() => ordemDoFluxo(esteira, departamentos), [esteira, departamentos]);
  const todosBlocos = useMemo(
    () => blocosPorFluxo(resp, ordem, recorte, comMovimento, universos, noRecorte),
    [resp, ordem, recorte, comMovimento, universos, noRecorte],
  );
  const blocos = useMemo(
    () => (fluxosSel.length ? todosBlocos.filter(b => fluxosSel.includes(b.fluxo)) : todosBlocos),
    [todosBlocos, fluxosSel],
  );
  // Bot em várias áreas aparece em cada bloco; ranking, destaques e gaps usam
  // UMA linha por bot, sobre a união dos universos dessas áreas.
  const linhas = useMemo(
    () => linhasPorBot(blocos, resp, recorte, comMovimento, universos, noRecorte),
    [blocos, resp, recorte, comMovimento, universos, noRecorte],
  );
  // Bot × dia: cada bot UMA vez, com o total real (as áreas dele somadas).
  const blocosMapa = useMemo(() => blocosSemRepeticao(blocos, linhas), [blocos, linhas]);
  const fluxosDia = useMemo(() => {
    const porFluxo = fluxosPorDia(resp, ordem, recorte, comMovimento, universos, noRecorte).filter(
      f => !fluxosSel.length || fluxosSel.includes(f.fluxo),
    );
    const total = totalDosFluxos(porFluxo);
    return total ? [...porFluxo, total] : porFluxo;
  }, [resp, ordem, recorte, comMovimento, fluxosSel, universos, noRecorte]);
  const gaps = useMemo(
    () =>
      gapsDosBots(
        linhas.map(l => ({ fluxo: l.fluxo, bots: [l] })),
        resp?.dias ?? [],
      ),
    [linhas, resp],
  );
  const dest = useMemo(() => calcularDestaques(linhas), [linhas]);
  const ranking = useMemo(() => ordenarRanking(linhas, criterio), [linhas, criterio]);
  const analistas = useMemo(
    () => porAnalista(blocos, empresas, responsaveis),
    [blocos, empresas, responsaveis],
  );
  const nomeDe = useMemo(() => {
    const m = new Map(empresas.map(e => [e.codi_emp, nomeEmpresa(e)]));
    return (codi: string) => m.get(codi) ?? codi;
  }, [empresas]);

  const dias = resp?.dias ?? [];

  return (
    <div style={fundoDaAba('analise')}>
      {/* ── Recortes ───────────────────────────────────────────────────────── */}
      <div
        style={{
          ...CARTAO,
          padding: '14px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={EYEBROW}>Fluxos</span>
          <div
            role="button"
            tabIndex={0}
            aria-pressed={!fluxosSel.length}
            onClick={() => setFluxosSel([])}
            style={{ ...estiloPilula(!fluxosSel.length, 'analise'), ...PILULA }}
          >
            Todos
          </div>
          {todosBlocos.map(b => {
            const ativo = fluxosSel.includes(b.fluxo);
            return (
              <div
                key={b.fluxo}
                role="button"
                tabIndex={0}
                aria-pressed={ativo}
                onClick={() =>
                  setFluxosSel(a => (ativo ? a.filter(x => x !== b.fluxo) : [...a, b.fluxo]))
                }
                style={{ ...estiloPilula(ativo, 'analise'), ...PILULA }}
              >
                {ativo ? '✓ ' : ''}
                {b.fluxo}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={EYEBROW}>Movimento</span>
          {(
            [
              ['todos', 'Todas as empresas'],
              ['com', 'Com movimento'],
              ['sem', 'Sem movimento'],
            ] as Array<[RecorteMovimento, string]>
          ).map(([v, rot]) => (
            <div
              key={v}
              role="button"
              tabIndex={0}
              aria-pressed={recorte === v}
              onClick={() => setRecorte(v)}
              title={
                v === 'todos'
                  ? undefined
                  : `Movimento = nota de saída ou de serviço na competência ${competenciaMov ?? ''}`
              }
              style={{
                ...estiloPilula(recorte === v, v === 'sem' ? 'erros' : 'analise'),
                ...PILULA,
                opacity: v !== 'todos' && !comMovimento ? 0.5 : 1,
              }}
            >
              {rot}
            </div>
          ))}
          {recorte !== 'todos' && (
            <span style={{ fontSize: '11px', color: C.sub }}>
              Tudo é refeito pelo recorte: empresas, conclusões, tentativas e tempo.
            </span>
          )}
        </div>
        {filtroAtivo && (
          <div style={{ fontSize: '11.5px', color: C.sub }}>
            <strong style={{ color: C.ink }}>Mesmo recorte do Processos:</strong>{' '}
            {descricaoDosFiltros.join(' · ') || 'filtros ativos'}. Para ver tudo, limpe os filtros
            na aba Processos.
          </div>
        )}
        {resp?.carteira_restrita && (
          <div style={{ fontSize: '11px', color: C.sub }}>
            Números da sua carteira. Horários e quantidade de execuções são do bot (globais).
          </div>
        )}
      </div>

      {isError && (
        <Faixa tom="err">
          Não deu para carregar a análise agora — tente atualizar ou trocar o período.
        </Faixa>
      )}
      {isLoading && !resp && (
        <div style={{ ...CARTAO, color: C.sub, fontSize: '12.5px' }}>
          Medindo o que cada bot fez em cada dia…
        </div>
      )}

      {resp && (
        <>
          <Destaques dest={dest} apelidoDoBot={apelidoDoBot} />

          {/* ── Fluxo por dia ─────────────────────────────────────────────── */}
          <section style={{ ...CARTAO, display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <TituloSecao
              aba="analise"
              icone={<Icone nome="processos" tamanho={17} />}
              titulo="Conclusão do fluxo, dia a dia"
              sub="Barra cheia: empresas que concluíram a ÚLTIMA etapa do fluxo no dia. Barra clara: as que passaram por algum bot dele. O número ao fim é o acumulado distinto do período."
            />
            {!fluxosDia.length && (
              <div style={{ fontSize: '12px', color: C.faint }}>Nenhum fluxo rodou no período.</div>
            )}
            {fluxosDia.map(f => {
              const maior = Math.max(1, ...f.dias.map(d => d.emAndamento));
              const ehTotal = f.fluxo === TOTAL_DOS_FLUXOS;
              return (
                <div
                  key={f.fluxo}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(140px, 200px) 1fr auto',
                    gap: '14px',
                    alignItems: 'end',
                    ...(ehTotal
                      ? { borderTop: '1px solid rgba(16,60,48,0.10)', paddingTop: '14px' }
                      : {}),
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: ehTotal ? 750 : 650 }}>
                      {f.fluxo}
                    </div>
                    <div style={{ fontSize: '10.5px', color: C.faint }}>
                      {ehTotal ? 'soma dos fluxos acima' : `fecha em: ${f.ultimaEtapa ?? '—'}`}
                    </div>
                  </div>
                  <div
                    style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '54px' }}
                  >
                    {f.dias.map(d => {
                      const r = rotuloDoDia(d.dia);
                      return (
                        <div
                          key={d.dia}
                          title={`${r.semana} ${r.data}: ${d.fecharam} fecharam · ${d.emAndamento} passaram pelo fluxo · ${d.acumulado} acumuladas`}
                          style={{
                            flex: 1,
                            minWidth: '6px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'flex-end',
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: `${(d.emAndamento / maior) * 100}%`,
                              borderRadius: '4px 4px 0 0',
                              background: `oklch(0.9 0.04 ${H})`,
                            }}
                          />
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              height: `${(d.fecharam / maior) * 100}%`,
                              borderRadius: '4px 4px 0 0',
                              background: `linear-gradient(180deg, oklch(0.66 0.14 ${H}), oklch(0.52 0.15 ${H}))`,
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em' }}>
                      {fmt.format(f.totalFecharam)}
                    </div>
                    <div style={{ fontSize: '10.5px', color: C.faint }}>fecharam no período</div>
                  </div>
                </div>
              );
            })}
          </section>

          {/* ── Mapa bot × dia ────────────────────────────────────────────── */}
          <section style={{ ...CARTAO, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <TituloSecao
              aba="analise"
              icone={<Icone nome="calendario" tamanho={17} />}
              titulo="Bot × dia"
              sub="Cada célula é quantas empresas o bot concluiu no dia. A cor é a taxa do dia (concluídas sobre as que ele tentou). Tracejado = não rodou. Clique para abrir o dia. Bot em várias áreas aparece uma vez, com o total delas somado."
            />
            <LegendaMapa />
            <MapaBotDia
              blocos={blocosMapa}
              dias={dias}
              apelidoDoBot={apelidoDoBot}
              onAbrir={(linha, dia) => setDetalhe({ linha, dia })}
            />
          </section>

          {/* ── Ranking ───────────────────────────────────────────────────── */}
          <section style={{ ...CARTAO, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <TituloSecao
              aba="analise"
              icone={<Icone nome="analise" tamanho={17} />}
              titulo="Quem rende mais"
              sub={`Empresas/hora = concluídas ÷ tempo de máquina gasto (todas as tentativas). Destaques de ritmo e tempo só com ${AMOSTRA_MINIMA}+ empresas concluídas.`}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={EYEBROW}>Ordenar por</span>
              {(
                [
                  ['concluidas', 'Mais concluiu'],
                  ['ritmo', 'Empresas por hora'],
                  ['tempo', 'Menor tempo por empresa'],
                  ['taxa', 'Taxa de sucesso'],
                  ['retentativa', 'Mais retentativa'],
                ] as Array<[CriterioRanking, string]>
              ).map(([v, rot]) => (
                <div
                  key={v}
                  role="button"
                  tabIndex={0}
                  aria-pressed={criterio === v}
                  onClick={() => setCriterio(v)}
                  style={{ ...estiloPilula(criterio === v, 'analise'), ...PILULA }}
                >
                  {rot}
                </div>
              ))}
            </div>
            <Ranking linhas={ranking} apelidoDoBot={apelidoDoBot} comMov={Boolean(comMovimento)} />
          </section>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
              gap: '18px',
            }}
          >
            <section style={{ ...CARTAO, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TituloSecao
                aba="erros"
                icone={<Icone nome="relogio" tamanho={17} />}
                titulo="Gaps"
                sub="Dias sem rodar entre a primeira e a última atividade, dias em que rodou e não concluiu nada, e há quantos dias está parado."
              />
              <Gaps gaps={gaps} apelidoDoBot={apelidoDoBot} />
            </section>

            <section style={{ ...CARTAO, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TituloSecao
                aba="resumo"
                icone={<Icone nome="empresa" tamanho={17} />}
                titulo="Por analista"
                sub="O responsável da área (fiscal, contábil, pessoal) de cada empresa, cruzado com os bots do fluxo da área. Ordenado por volume de empresas."
              />
              <Analistas linhas={analistas} apelidoDoBot={apelidoDoBot} />
            </section>
          </div>
        </>
      )}

      {detalhe && (
        <ModalDia
          linha={detalhe.linha}
          dia={detalhe.dia}
          d={detalhe.linha.porDia.get(detalhe.dia)}
          apelidoDoBot={apelidoDoBot}
          nomeDe={nomeDe}
          comMovimento={comMovimento}
          onFechar={() => setDetalhe(null)}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PEÇAS
// ═══════════════════════════════════════════════════════════════════════════

type Apelido = (bot: string) => string; // eslint-disable-line no-unused-vars

const Destaques: React.FC<{
  dest: ReturnType<typeof calcularDestaques>;
  apelidoDoBot: Apelido;
}> = ({ dest, apelidoDoBot }) => {
  const itens = [
    {
      rotulo: 'Mais concluiu',
      l: dest.maisConcluiu,
      valor: (l: LinhaBot) => `${fmt.format(l.concluidas)} empresas`,
      matiz: 162,
      icone: 'check',
    },
    {
      rotulo: 'Mais rápido por empresa',
      l: dest.maisRapido,
      valor: (l: LinhaBot) => duracaoCurta(l.totais.tempo_medio_ok_ms),
      matiz: 240,
      icone: 'raio',
    },
    {
      rotulo: 'Melhor ritmo',
      l: dest.melhorRitmo,
      valor: (l: LinhaBot) => `${fmt1.format(l.totais.empresas_por_hora ?? 0)} empresas/h`,
      matiz: H,
      icone: 'analise',
    },
    {
      rotulo: 'Mais retentativa',
      l: dest.maisRetentativa,
      valor: (l: LinhaBot) =>
        `${fmt1.format(l.totais.tentativas_por_empresa ?? 0)} tentativas/empresa`,
      matiz: 25,
      icone: 'alerta',
    },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))',
        gap: '14px',
      }}
    >
      {itens.map(it => (
        <div
          key={it.rotulo}
          style={{ ...CARTAO, display: 'flex', gap: '12px', alignItems: 'flex-start', minWidth: 0 }}
        >
          <span
            aria-hidden
            style={{
              flex: 'none',
              width: '36px',
              height: '36px',
              borderRadius: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              background: `linear-gradient(140deg, oklch(0.68 0.15 ${it.matiz}), oklch(0.52 0.15 ${it.matiz}))`,
              boxShadow: `0 6px 16px oklch(0.55 0.15 ${it.matiz} / 0.3)`,
            }}
          >
            <Icone nome={it.icone} tamanho={17} />
          </span>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={EYEBROW}>{it.rotulo}</span>
            <span
              title={it.l?.bot}
              style={{
                fontSize: '15px',
                fontWeight: 700,
                color: C.ink,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {it.l ? apelidoDoBot(it.l.bot) : '—'}
            </span>
            <span style={{ fontSize: '12px', color: C.sub }}>
              {it.l ? it.valor(it.l) : 'sem amostra suficiente'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const LegendaMapa: React.FC = () => (
  <div
    style={{ display: 'flex', gap: '6px 14px', flexWrap: 'wrap', fontSize: '11px', color: C.sub }}
  >
    {(
      [
        ['ok', '100% concluídas'],
        ['run', '75–99%'],
        ['warn', '40–74%'],
        ['err', 'menos de 40%'],
        ['idle', 'rodou sem empresa'],
        ['vazio', 'não rodou'],
      ] as Array<[TomCelula, string]>
    ).map(([k, rot]) => (
      <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
        <span
          style={{
            width: '14px',
            height: '14px',
            borderRadius: '4px',
            background: COR_CELULA[k].bg,
            border: COR_CELULA[k].borda,
            boxSizing: 'border-box',
          }}
        />
        {rot}
      </span>
    ))}
  </div>
);

const LARGURA_BOT = 230;
const LARGURA_DIA = 38;

const MapaBotDia: React.FC<{
  blocos: BlocoFluxo[];
  dias: string[];
  apelidoDoBot: Apelido;
  // eslint-disable-next-line no-unused-vars
  onAbrir: (linha: LinhaBot, dia: string) => void;
}> = ({ blocos, dias, apelidoDoBot, onAbrir }) => {
  const colunas = `${LARGURA_BOT}px repeat(${dias.length}, ${LARGURA_DIA}px) 70px`;
  return (
    <div style={{ overflowX: 'auto', paddingBottom: '6px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: colunas,
          gap: '3px',
          minWidth: 'max-content',
        }}
      >
        <div />
        {dias.map(d => {
          const r = rotuloDoDia(d);
          const fimDeSemana = r.semana === 'sáb' || r.semana === 'dom';
          return (
            <div
              key={d}
              style={{
                textAlign: 'center',
                fontFamily: FONTE_MONO,
                fontSize: '9.5px',
                color: fimDeSemana ? C.faint : C.sub,
                lineHeight: 1.25,
              }}
            >
              {r.semana}
              <br />
              {r.data.slice(0, 2)}
            </div>
          );
        })}
        <div style={{ ...EYEBROW, textAlign: 'right', alignSelf: 'end' }}>Total</div>

        {blocos.map(bl => (
          <React.Fragment key={bl.fluxo}>
            <div
              style={{
                gridColumn: '1 / -1',
                marginTop: '10px',
                padding: '5px 10px',
                borderRadius: '9px',
                background: `oklch(0.95 0.03 ${H} / 0.8)`,
                fontSize: '12px',
                fontWeight: 700,
                color: `oklch(0.42 0.1 ${H})`,
              }}
            >
              {bl.fluxo}
              <span style={{ fontWeight: 500, color: C.sub }}> · {bl.bots.length} bots</span>
            </div>
            {bl.bots.map(l => (
              <React.Fragment key={l.bot}>
                <div
                  title={l.bot}
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    background: 'rgba(255,255,255,0.92)',
                    borderRadius: '8px',
                    padding: '3px 8px',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {apelidoDoBot(l.bot)}
                  </span>
                  {(l.areas || l.grupo) && (
                    <span
                      title={l.areas ? `Total somando: ${l.areas.join(', ')}` : undefined}
                      style={{
                        fontSize: '10px',
                        color: l.areas ? `oklch(0.45 0.1 ${H})` : C.faint,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {l.areas ? `${l.areas.length} áreas: ${l.areas.join(' · ')}` : l.grupo}
                    </span>
                  )}
                </div>
                {dias.map(dia => {
                  const d = l.porDia.get(dia);
                  const tom = tomDaCelula(d);
                  const cor = COR_CELULA[tom];
                  const r = rotuloDoDia(dia);
                  return (
                    <div
                      key={dia}
                      onClick={() => tom !== 'vazio' && onAbrir(l, dia)}
                      title={
                        d && tom !== 'vazio'
                          ? `${r.semana} ${r.data} — das ${d.empresas} empresas que rodaram, ${d.concluidas} terminaram o dia concluídas${d.puladas ? ` (${d.puladas} puladas: nada a fazer)` : ''} e ${d.so_falha} em falha · ${fmt.format(d.tentativas)} tentativas, ${d.execucoes} execução(ões)`
                          : `${r.semana} ${r.data} — não rodou`
                      }
                      style={{
                        height: '30px',
                        borderRadius: '7px',
                        background: cor.bg,
                        border: cor.borda,
                        color: cor.fg,
                        fontFamily: FONTE_MONO,
                        fontSize: '10.5px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: tom === 'vazio' ? 'default' : 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      {tom === 'vazio' ? '' : d?.concluidas}
                    </div>
                  );
                })}
                <div
                  style={{
                    textAlign: 'right',
                    alignSelf: 'center',
                    fontFamily: FONTE_MONO,
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                  title={
                    l.deUniverso
                      ? `Como no Processos: ${l.concluidas} concluídas e ${l.falhas} com erro ou alerta, das ${l.empresas} empresas do regime do fluxo (último desfecho no período)`
                      : `${l.concluidas} concluídas e ${l.falhas} com erro ou alerta, das ${l.empresas} empresas em que o bot rodou (último desfecho no período)`
                  }
                >
                  {fmt.format(l.concluidas)}
                  <div style={{ fontSize: '9.5px', fontWeight: 500, color: C.faint }}>
                    de {fmt.format(l.empresas)}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const TH: React.CSSProperties = {
  ...EYEBROW,
  fontSize: '9.5px',
  textAlign: 'right',
  padding: '6px 8px',
  whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = {
  padding: '7px 8px',
  textAlign: 'right',
  fontFamily: FONTE_MONO,
  fontSize: '11.5px',
  whiteSpace: 'nowrap',
};

const Barra: React.FC<{ pct: number; matiz: number }> = ({ pct, matiz }) => (
  <span
    style={{
      width: '64px',
      height: '6px',
      borderRadius: '999px',
      background: 'rgba(16,60,48,0.08)',
      overflow: 'hidden',
      display: 'inline-block',
      verticalAlign: 'middle',
      marginRight: '6px',
    }}
  >
    <span
      style={{
        display: 'block',
        width: `${Math.max(0, Math.min(100, pct))}%`,
        height: '100%',
        background: `oklch(0.6 0.14 ${matiz})`,
      }}
    />
  </span>
);

const corDaTaxa = (t: number | null): string => {
  if (t === null) return C.faint;
  if (t >= 100) return C.greenFg;
  if (t >= 75) return C.blueFg;
  if (t >= 40) return C.amberFg;
  return C.redFg;
};

const Ranking: React.FC<{ linhas: LinhaBot[]; apelidoDoBot: Apelido; comMov: boolean }> = ({
  linhas,
  apelidoDoBot,
  comMov,
}) => {
  const maior = Math.max(1, ...linhas.map(l => l.concluidas));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONTE_TEXTO }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left' }}>#</th>
            <th style={{ ...TH, textAlign: 'left' }}>Bot</th>
            <th style={TH}>Concluídas</th>
            <th style={TH}>Taxa</th>
            <th style={TH}>Tempo/empresa</th>
            <th style={TH}>Empresas/h</th>
            <th style={TH}>Tentativas/empresa</th>
            <th style={TH}>Dias rodando</th>
            {comMov && <th style={TH}>Com / sem mov.</th>}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => {
            const retentativaAlta = (l.totais.tentativas_por_empresa ?? 0) >= 5;
            return (
              <tr key={l.bot} style={{ borderTop: '1px solid rgba(16,60,48,0.07)' }}>
                <td style={{ ...TD, textAlign: 'left', color: C.faint }}>{i + 1}</td>
                <td style={{ ...TD, textAlign: 'left', fontFamily: FONTE_TEXTO }} title={l.bot}>
                  <div style={{ fontSize: '12.5px', fontWeight: 600 }}>{apelidoDoBot(l.bot)}</div>
                  <div style={{ fontSize: '10px', color: C.faint }}>{l.fluxo}</div>
                </td>
                <td style={TD}>
                  <Barra pct={(l.concluidas / maior) * 100} matiz={H} />
                  {fmt.format(l.concluidas)}
                  <span style={{ color: C.faint }}> / {fmt.format(l.empresas)}</span>
                </td>
                <td style={{ ...TD, color: corDaTaxa(l.taxa), fontWeight: 700 }}>
                  {l.taxa === null ? '—' : `${fmt1.format(l.taxa)}%`}
                </td>
                <td style={TD}>{duracaoCurta(l.totais.tempo_medio_ok_ms)}</td>
                <td style={TD}>
                  {l.totais.empresas_por_hora === null
                    ? '—'
                    : fmt1.format(l.totais.empresas_por_hora)}
                </td>
                <td
                  style={{
                    ...TD,
                    color: retentativaAlta ? C.redFg : C.ink,
                    fontWeight: retentativaAlta ? 700 : 400,
                  }}
                >
                  {l.totais.tentativas_por_empresa === null
                    ? '—'
                    : fmt1.format(l.totais.tentativas_por_empresa)}
                </td>
                <td style={TD}>{l.totais.dias_com_execucao}</td>
                {comMov && (
                  <td style={TD}>
                    <span style={{ color: C.blueFg }}>{fmt.format(l.comMovimento)}</span>
                    <span style={{ color: C.faint }}> / </span>
                    <span style={{ color: C.redFg }}>{fmt.format(l.semMovimento)}</span>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const chip = (tom: 'warn' | 'err' | 'idle'): React.CSSProperties => ({
  fontSize: '10.5px',
  padding: '2px 8px',
  borderRadius: '999px',
  background: TONES[tom].bg,
  color: TONES[tom].fg,
  fontWeight: 600,
});

const ITEM_LISTA: React.CSSProperties = {
  padding: '8px 11px',
  borderRadius: '11px',
  background: 'rgba(255,255,255,0.6)',
  border: '1px solid rgba(255,255,255,0.9)',
};

const Gaps: React.FC<{ gaps: ReturnType<typeof gapsDosBots>; apelidoDoBot: Apelido }> = ({
  gaps,
  apelidoDoBot,
}) => {
  if (!gaps.length) {
    return (
      <Faixa tom="ok">
        Nenhum gap no período: todo bot rodou e concluiu nos dias em que estava ativo.
      </Faixa>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        maxHeight: '420px',
        overflowY: 'auto',
      }}
    >
      {gaps.slice(0, 30).map(g => {
        const seq = g.maiorSequencia;
        const textoSeq =
          seq && seq.dias > 1
            ? ` · maior: ${seq.dias} seguidos (${rotuloDoDia(seq.de).data}–${rotuloDoDia(seq.ate).data})`
            : '';
        return (
          <div
            key={g.bot}
            style={{ ...ITEM_LISTA, display: 'flex', flexDirection: 'column', gap: '3px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <span style={{ fontSize: '12.5px', fontWeight: 650 }} title={g.bot}>
                {apelidoDoBot(g.bot)}
              </span>
              <span style={{ fontSize: '10.5px', color: C.faint }}>{g.fluxo}</span>
            </div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {g.diasParado.length > 0 && (
                <span style={chip('warn')}>
                  {`${g.diasParado.length} dia(s) sem rodar${textoSeq}`}
                </span>
              )}
              {g.rodouSemConcluir.length > 0 && (
                <span style={chip('err')}>
                  {`rodou sem concluir em ${g.rodouSemConcluir.length} dia(s)`}
                </span>
              )}
              {g.paradoDesde > 0 && (
                <span style={chip('idle')}>{`parado há ${g.paradoDesde} dia(s)`}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Analistas: React.FC<{ linhas: ReturnType<typeof porAnalista>; apelidoDoBot: Apelido }> = ({
  linhas,
  apelidoDoBot,
}) => {
  if (!linhas.length) {
    return (
      <div style={{ fontSize: '12px', color: C.faint }}>
        Sem responsável cadastrado nas empresas que os bots tocaram no período.
      </div>
    );
  }
  const maior = Math.max(1, ...linhas.map(l => l.empresas));
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        maxHeight: '420px',
        overflowY: 'auto',
      }}
    >
      {linhas.slice(0, 40).map(l => (
        <div
          key={`${l.area}|${l.analista}`}
          style={{
            ...ITEM_LISTA,
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '4px 10px',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: '12.5px', fontWeight: 650 }}>{l.analista}</span>
            <span style={{ fontSize: '10.5px', color: C.faint }}> · {l.area}</span>
          </div>
          <div style={{ fontFamily: FONTE_MONO, fontSize: '11.5px', textAlign: 'right' }}>
            <Barra pct={(l.empresas / maior) * 100} matiz={240} />
            {fmt.format(l.concluidas)}
            <span style={{ color: C.faint }}> / {fmt.format(l.empresas)}</span>
            <span style={{ color: C.sub }}>
              {l.taxa === null ? '' : ` · ${fmt1.format(l.taxa)}%`}
            </span>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {l.topBots.map((b, i) => (
              <span
                key={b.bot}
                title={`${b.bot}: ${b.concluidas} de ${b.empresas} empresas da carteira`}
                style={{
                  fontSize: '10.5px',
                  padding: '2px 8px',
                  borderRadius: '999px',
                  background: i === 0 ? `oklch(0.93 0.05 ${H})` : 'rgba(16,60,48,0.05)',
                  color: i === 0 ? `oklch(0.42 0.12 ${H})` : C.sub,
                  fontWeight: 600,
                }}
              >
                {`${apelidoDoBot(b.bot)} · ${b.concluidas}/${b.empresas}`}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ModalDia: React.FC<{
  linha: LinhaBot;
  dia: string;
  d: IBotDia | undefined;
  apelidoDoBot: Apelido;
  // eslint-disable-next-line no-unused-vars
  nomeDe: (c: string) => string;
  comMovimento: Set<string> | null;
  onFechar: () => void;
}> = ({ linha, dia, d, apelidoDoBot, nomeDe, comMovimento, onFechar }) => {
  useEffect(() => {
    const f = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [onFechar]);
  const r = rotuloDoDia(dia);
  const lista = (titulo: string, codis: string[], tom: 'ok' | 'err') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
      <span style={EYEBROW}>{`${titulo} · ${codis.length}`}</span>
      <div
        style={{
          maxHeight: '260px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        {!codis.length && <span style={{ fontSize: '11.5px', color: C.faint }}>nenhuma</span>}
        {codis.map(c => (
          <div
            key={c}
            style={{ display: 'flex', gap: '7px', fontSize: '11.5px', alignItems: 'baseline' }}
          >
            <span
              style={{
                fontFamily: FONTE_MONO,
                color: TONES[tom].fg,
                fontWeight: 700,
                flex: 'none',
              }}
            >
              {c}
            </span>
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: C.ink,
              }}
            >
              {nomeDe(c) !== c ? nomeDe(c) : ''}
            </span>
            {comMovimento && (
              <span
                style={{
                  marginLeft: 'auto',
                  flex: 'none',
                  fontSize: '10px',
                  color: comMovimento.has(c) ? C.blueFg : C.redFg,
                }}
              >
                {comMovimento.has(c) ? 'com mov.' : 'sem mov.'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
  const janela = d
    ? `${horaLocal(d.inicio_exec ?? d.primeira)} – ${horaLocal(d.fim_exec ?? d.ultima)}`
    : '—';
  return (
    <div
      role="presentation"
      onClick={onFechar}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        background: 'oklch(0.2 0.02 165 / 0.38)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: FONTE_TEXTO,
        color: C.ink,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${apelidoDoBot(linha.bot)} — ${r.data}`}
        onClick={ev => ev.stopPropagation()}
        style={{
          ...VIDRO,
          background: 'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(255,255,255,0.88))',
          borderRadius: '22px',
          width: '100%',
          maxWidth: '820px',
          maxHeight: '88vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: '20px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={EYEBROW}>{`${linha.fluxo} · ${r.semana} ${r.data}`}</div>
            <div style={{ fontSize: '18px', fontWeight: 700, marginTop: '3px' }}>
              {apelidoDoBot(linha.bot)}
            </div>
          </div>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            style={{
              border: 'none',
              background: 'rgba(16,60,48,0.06)',
              borderRadius: '10px',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              color: C.sub,
            }}
          >
            ✕
          </button>
        </div>
        {d && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '10px',
              }}
            >
              {[
                ['Concluídas', `${d.concluidas} de ${d.empresas}`],
                ['Só com falha', String(d.so_falha)],
                ['Tentativas', fmt.format(d.tentativas)],
                ['Tempo/empresa', duracaoCurta(d.tempo_medio_ok_ms)],
                ['Execuções', String(d.execucoes)],
                ['Janela', janela],
              ].map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    padding: '9px 11px',
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(255,255,255,0.95)',
                  }}
                >
                  <div style={EYEBROW}>{k}</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, marginTop: '2px' }}>{v}</div>
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
                gap: '16px',
              }}
            >
              {lista('Concluídas', d.ok_codis, 'ok')}
              {lista('Só com falha', d.falha_codis, 'err')}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
